import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setContext, getCurrentScope } from '@sentry/react';
import styled, { css } from 'styled-components';
import { animated, useTransition } from 'react-spring';
import { isNil } from 'ramda';

import {
  PostMessage,
  Position,
  LayoutT,
  DHApp,
  WidgetVisibilityAction,
} from '~/shared/types';

import log from '~/shared/utils/log';
import cleanPath from '../../utils/cleanPath';
import { ROOT } from '../../constants/paths';

import TriggerComponent from '../../components/TriggerComponent';
import Header, { HeaderAction } from '../../components/Header';
import Footer from '../../components/Footer';

import useWidgetContext from '../../hooks/useWidgetContext';
import useMessaging from '../../hooks/useMessaging';
import useDismiss from '../../hooks/useDismiss';
import useCopy from '../../hooks/useCopy';
import usePathPrefix from '../../hooks/usePathPrefix';
import useFireTrackingEvent from '../../hooks/useFireTrackingEvent';
// import { usePosthog as usePosthogContext } from '~/widget/context/PosthogContext';

import useFrameSizeSelector from '../../hooks/useFrameSizeSelector';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  hostPageDimensionsState,
  layoutSelector,
  widgetState,
} from '../../state/widgetState';
import { getNextAppToSuggest } from '~/widget/appSuggester/db/getNextAppToSuggest';
import getIframeStyle from '~/widget/components/WithConfig/getIFrameStyle';

// import safeLocalStorage, {
//   canAccessLocalStorage,
// } from '../../utils/safeLocalStorage';
// import useTracking from '../../hooks/useTracking';
export type Props = {};

/**
 * Extra pixels to add to the height and width of the widget
 * to avoid scrollbars and other UI elements from overflowing
 * the widget.
 */
const EXTRA_PIXELS = 0;

const IframeLayout: React.FC<Props> = ({ ...rest }) => {
  const navigate = useNavigate();
  const pageParams = useParams();
  const location = useLocation();
  const pathPrefix = usePathPrefix();
  const fireTrackingEvent = useFireTrackingEvent();
  const context = useWidgetContext();
  const [highlighted, setHighlighted] = useState<DHApp | null>(null);
  // const { getFeatureFlag } = usePosthogContext();
  // const rotateFlag = getFeatureFlag('widget---rotating-highlighted-app');
  // const rotateEnabled = rotateFlag === 'rotation';
  // const doTrack = useTracking();

  const { positions, apps, appearances: sizes } = context;
  const { actions: actionLabels } = useCopy();

  const [{ expanded, fullscreen, appsOpened }, setWidgetState] =
    useRecoilState(widgetState);

  // useEffect(() => {
  //   if (!canAccessLocalStorage()) return;
  //   const accountId = window.__DH_CONFIG?.accountId || 'unknown';

  //   const shouldSend = (
  //     key: string,
  //     offsetDays: number,
  //     accountId: string,
  //   ): boolean => {
  //     const STORAGE_KEY = `${key}_timestamp_${accountId}`;
  //     const offsetMs = offsetDays * 24 * 60 * 60 * 1000; // offset in days
  //     const now = Date.now();
  //     const lastTimestampStr = safeLocalStorage.getItem(STORAGE_KEY);
  //     const lastTimestamp = lastTimestampStr
  //       ? parseInt(lastTimestampStr, 10)
  //       : 0;

  //     const timeSinceLastSend = now - lastTimestamp;
  //     if (timeSinceLastSend < offsetMs) return false;

  //     safeLocalStorage.setItem(STORAGE_KEY, now.toString());
  //     return true;
  //   };

  //   if (shouldSend('wgt_loaded_30_days_new', 30, accountId)) {
  //     doTrack({ name: 'wgt_loaded_monthly' });
  //   }
  // }, [doTrack]);

  const contextAware = context.contextAware !== false;

  useEffect(() => {
    if (expanded) return;

    if (!highlighted) {
      (async () => {
        const appsF = Object.values(context.apps);
        const nextApp = await getNextAppToSuggest({
          previousApp: null,
          apps: appsF,
          options: {
            context: {
              accountId: context.accountId,
            },
            contextAware,
          },
        });

        setHighlighted(nextApp);
      })();

      return;
    }

    /** Only rotate when contextAware is true */
    if (!contextAware) return;

    const timeout = setTimeout(async () => {
      const appsF = Object.values(context.apps);

      const nextApp = await getNextAppToSuggest({
        previousApp: highlighted,
        apps: appsF,
        options: {
          context: {
            accountId: context.accountId,
          },
          contextAware,
        },
      });

      setHighlighted(nextApp);
    }, 12000);

    return () => {
      clearTimeout(timeout);
    };
  }, [apps, highlighted, expanded, contextAware]);

  const [isDismissed, onDismiss, onClearDismiss] = useDismiss();
  const [hiddenByVisibilityRules, setHiddenByVisibilityRules] = useState(false);
  const frameSizeSelector = useFrameSizeSelector();

  const layout = useRecoilValue(layoutSelector);

  const currentTriggerAppearance = sizes[frameSizeSelector];
  const { width: hostPageWidth } = useRecoilValue(hostPageDimensionsState);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousContainerDimensions = useRef<{
    width: number;
    height: number;
  } | null>(null);

  const root = pathPrefix + ROOT;

  const onMessage = useCallback(
    (message: PostMessage) => {
      log('Received message - widget iFrame Layout', message);

      switch (message.type) {
        case 'open':
          let pathname = `${pathPrefix}${message.payload.path ?? ROOT}`;
          const cleanedPath = cleanPath(pathname);
          const layout = cleanedPath === ROOT ? 'narrow' : 'wide';

          onClearDismiss();
          setWidgetState((prev) => ({
            ...prev,
            layout,
            expanded: true,
          }));

          const matched = message.payload.path?.match(/^\/apps\/(.+)/);
          // If the app is not inside the app context its not enabled we we redirect to not found page

          if (matched && matched[1] && isNil(context.apps[matched[1]])) {
            pathname = `${pathPrefix}/not-found`;
          }

          const params = new URLSearchParams({
            queryParams: message.payload.queryParams ?? '',
          });
          navigate(`${pathname}?${params.toString()}`);

          const eventName = (():
            | 'DeepLink'
            | 'ExpandWidget'
            | 'OpenCommand' => {
            if (message.payload.triggerType === 'deepLink') return 'DeepLink';
            if (message.payload.triggerType === 'command') return 'OpenCommand';
            return 'ExpandWidget';
          })();

          fireTrackingEvent({
            event: eventName,
            path: message.payload.path ?? ROOT,
            triggerType: message.payload.triggerType,
          });
          break;
        case 'close':
          setWidgetState((prev) => ({ ...prev, expanded: false }));
          navigate(`${root}${location.search}`);
          fireTrackingEvent({ event: 'CollapseWidget' });
          break;
        case 'track': {
          const slug = pageParams.slug;
          const app =
            slug && typeof slug === 'string' ? context.apps[slug] : null;

          if (app) {
            fireTrackingEvent({
              event: 'TrackMessage',
              message,
            });
          }
          break;
        }
        case 'clearDismiss':
          onClearDismiss();
          navigate(`${root}${location.search}`);
          break;

        case 'Bootstrap_VisibilityRuleAction': {
          const { action } = message.payload;
          if (action === WidgetVisibilityAction.Hide) {
            sendMessage({
              type: 'Widget_Resize',
              payload: {
                style: 'opacity: 0; width: 0px; height: 0px;',
                preventBackgroundScroll: false,
              },
            });
            setHiddenByVisibilityRules(true);
          } else {
            setHiddenByVisibilityRules(false);
          }
          break;
        }

        // Forward message up to bootstrap and external window
        case 'iframeBridgeLoaded':
          sendMessage({ type: 'iframeBridgeLoaded' });
          break;
        default:
          break;
      }
    },
    [fireTrackingEvent, onClearDismiss, pathPrefix, root, pageParams],
  );

  const sendMessage = useMessaging(onMessage);

  /**
   * Resizing the bootstrap iframe based on the trigger container size
   */
  useEffect(() => {
    const el = containerRef?.current;
    if (!el || expanded) return;

    const sendResize = ({
      width,
      height,
    }: {
      width: number;
      height: number;
    }) => {
      if (previousContainerDimensions.current) {
        if (
          previousContainerDimensions.current.width === width &&
          previousContainerDimensions.current.height === height
        )
          return;
      }
      previousContainerDimensions.current = { width, height };

      const finalHeight = Math.ceil(height + EXTRA_PIXELS) + 'px';
      const finalWidth =
        Math.min(hostPageWidth, Math.ceil(width + EXTRA_PIXELS)) + 'px';

      sendMessage({
        type: 'Widget_Resize',
        payload: {
          style: `width: ${finalWidth}; height: ${finalHeight}; ${getIframeStyle({ position: positions[frameSizeSelector], size: frameSizeSelector })}`,
          preventBackgroundScroll: false,
        },
      });
    };

    if (previousContainerDimensions.current == null) {
      sendResize({
        width: el.getBoundingClientRect().width,
        height: el.getBoundingClientRect().height,
      });
    }

    let shrinkTimeout: number | null = null;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        if (shrinkTimeout !== null) {
          window.clearTimeout(shrinkTimeout);
        }

        /** Always send a last - correct size */
        shrinkTimeout = window.setTimeout(() => {
          sendResize({ width, height });
        }, 200);

        // When the widget gets smaller, only
        // rely on the last - correct size
        if (
          previousContainerDimensions.current !== null &&
          height < previousContainerDimensions.current.height
        ) {
          continue;
        }

        if (
          previousContainerDimensions.current !== null &&
          height > previousContainerDimensions.current.height
        ) {
          // Growing (or first measurement) can be sent immediately.
          sendResize({ width, height: height + 50 });
        }
      }
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      if (shrinkTimeout !== null) {
        window.clearTimeout(shrinkTimeout);
      }
    };
  }, [expanded, containerRef?.current, frameSizeSelector]);

  const visibleApps = Object.values(apps).filter((app) => !app.hidden);
  const hasOnlyOneVisibleApp = visibleApps.length === 1;

  const slug = pageParams.slug;
  const currentApp =
    slug && typeof slug === 'string' ? context.apps[slug] : null;

  const isFullscreen = (() => {
    if (!expanded) return false;
    if (fullscreen) return true;
    if (frameSizeSelector === 'mobile') return true;
    if (layout === 'wide') return true;

    return false;
  })();

  const hasPadding = (() => {
    if (!expanded) return false;
    if (frameSizeSelector === 'mobile') return false;
    if (layout === 'wide') return true;
    if (layout === 'narrow') return true;

    return false;
  })();

  useEffect(() => {
    if (isDismissed) return;
    if (hiddenByVisibilityRules) return;

    const payload = ((): {
      style: string;
      preventBackgroundScroll: boolean;
    } | null => {
      if (isFullscreen) {
        const basePosition = positions[frameSizeSelector].endsWith('Right')
          ? 'bottom: 0px; right: 0px;'
          : 'bottom: 0px; left: 0px;';

        return {
          style: `width: 100%; height: 100%; ${basePosition}`,
          preventBackgroundScroll: true,
        };
      }

      if (layout === 'narrow') {
        const basePosition = positions[frameSizeSelector].endsWith('Right')
          ? `bottom: 0px; right: 0px;`
          : `bottom: 0px; left: 0px;`;

        const basePadding = 16;
        return {
          style: `width: calc(320px + ${basePadding * 2}px); height: calc(95% + ${basePadding * 2}px); ${basePosition}`,
          preventBackgroundScroll: false,
        };
      }

      return null;
    })();

    if (payload === null) return;

    sendMessage({
      type: 'Widget_Resize',
      payload,
    });
  }, [
    fullscreen,
    layout,
    sendMessage,
    expanded,
    frameSizeSelector,
    positions,
    currentTriggerAppearance,
    hasOnlyOneVisibleApp,
    isDismissed,
    hiddenByVisibilityRules,
    location,
  ]);

  useEffect(() => {
    setContext('widget-context', {
      ...context,
    });

    return () => {
      // When we unmount the layout we need to clear the scope
      // https://docs.sentry.io/platforms/javascript/guides/nextjs/enriching-events/context/#clearing-context
      getCurrentScope().clear();
    };
  }, [context]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && layout === 'fullscreen') {
        setWidgetState((prev) => ({
          ...prev,
          fullscreen: false,
          layout: 'wide',
        }));
      }
    },
    [layout],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const cleanedPath = cleanPath(location.pathname);
    fireTrackingEvent({
      event: 'VirtualPageView',
      path: cleanedPath,
    });
  }, [location, fireTrackingEvent]);

  const noShow =
    isDismissed === true ||
    hiddenByVisibilityRules === true ||
    (context.preventDefaultOpening === true && expanded === false);

  const transitions = useTransition(expanded, {
    from: {
      opacity: 0,
      height: '0',
    },
    enter: {
      opacity: 1,
      height: '100%',
    },
    leave: {
      opacity: 0,
      height: '0',
    },
    config: {
      tension: 180,
      mass: 1,
      friction: 10,
      clamp: true,
    },
  });

  const actions: Array<HeaderAction> = [
    {
      label: actionLabels.goBackToOverview,
      icon: 'arrow-left',
      onClick: () => {
        if (hasOnlyOneVisibleApp) {
          setWidgetState((prev) => ({
            ...prev,
            expanded: false,
            layout: 'collapsed',
          }));
          return navigate(`${root}${location.search}`);
        }

        setWidgetState((prev) => ({
          ...prev,
          fullscreen: false,
          layout: 'narrow',
        }));

        return navigate(`${root}${location.search}`);
      },
    },
  ];

  if (frameSizeSelector !== 'mobile') {
    actions.push({
      label: actionLabels.fullscreen,
      icon: 'fullscreen',
      onClick: () =>
        setWidgetState((prev) => ({ ...prev, fullscreen: !prev.fullscreen })),
    });
  }

  const onClose = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      expanded: false,
      layout: 'collapsed',
    }));

    fireTrackingEvent({
      event: 'CollapseWidget',
    });

    navigate(`${root}${location.search}`);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [layout, frameSizeSelector, currentTriggerAppearance]);

  if (noShow) {
    return null;
  }

  if (expanded) {
    return (
      <Container id="container" $hasPadding={hasPadding}>
        {transitions(
          (styles, expanded) =>
            expanded === true && (
              <Overlay
                {...rest}
                $layout={layout}
                $position={positions[frameSizeSelector]}
              >
                <WidgetContainer
                  $fullscreen={isFullscreen && !hasPadding}
                  style={styles}
                >
                  <Header
                    appearance={layout}
                    onClose={onClose}
                    actions={actions}
                  />
                  <Inner
                    id="inner-container"
                    $bypassResizer={
                      currentApp ? currentApp.bypassResizer : false
                    }
                  >
                    <Outlet />
                  </Inner>
                  <Footer />
                </WidgetContainer>
              </Overlay>
            ),
        )}
      </Container>
    );
  }

  if (visibleApps.length === 0 || !highlighted) {
    return null;
  }

  return (
    <TriggerContainer
      id="container"
      ref={(node) => {
        if (containerRef.current === node) return;
        if (!node) return;

        containerRef.current = node;

        const { height, width } = node.getBoundingClientRect();

        sendMessage({
          type: 'Widget_Resize',
          payload: {
            style: `width: ${Math.ceil(width + EXTRA_PIXELS)}px; height: ${Math.ceil(height + EXTRA_PIXELS)}px; ${getIframeStyle({ position: positions[frameSizeSelector], size: frameSizeSelector })}`,
            preventBackgroundScroll: false,
          },
        });
      }}
    >
      <TriggerComponent
        dataTestId="layout-trigger-button"
        size={currentTriggerAppearance}
        position={positions[frameSizeSelector]}
        highlight={highlighted}
        onDismiss={onDismiss}
        hasOnlyOneApp={hasOnlyOneVisibleApp}
        onTrigger={(path) => {
          const nextPath = `${pathPrefix}${path}`;
          const cleanedPath = cleanPath(nextPath);

          const nextAppsOpened =
            cleanedPath !== ROOT && !appsOpened.includes(cleanedPath)
              ? appsOpened
              : [...appsOpened, cleanedPath];

          navigate(`${pathPrefix}${path}${location.search}`);
          setWidgetState((prev) => ({
            ...prev,
            expanded: true,
            layout: cleanedPath === ROOT ? 'narrow' : 'wide',
            appsOpened: nextAppsOpened,
          }));
        }}
      />
    </TriggerContainer>
  );
};

const Container = styled.div<{ $hasPadding: boolean }>(
  ({ $hasPadding }) => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;

    display: flex;
    justify-content: flex-start;
    align-items: flex-start;

    padding: ${$hasPadding ? '16px' : '0'};

    width: 100%;
    height: 100%;
  `,
);

const TriggerContainer = styled.div(
  () => css`
    position: relative;
    display: inline-block;
    overflow: hidden;
  `,
);

const Overlay = styled.div<{
  $layout: LayoutT;
  $position: Position;
}>(
  () => css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-end;
    align-self: flex-start;
  `,
);

export const scrollBarStyles = css`
  &::-webkit-scrollbar {
    width: 0.5em;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.color('secondary')};
    border-radius: ${({ theme }) => theme.getSystem().border.radius.base};
    outline: none;
  }
`;

const WidgetContainer = styled(animated.div)<{
  $fullscreen: boolean;
}>(
  ({ theme, $fullscreen }) => css`
    /* This is used to forbid overriding widget with the forced dark mode. */
    color-scheme: only light;
    background-color: #fff;
    border-radius: ${$fullscreen ? '0' : theme.getSystem().border.radius.base};
    box-shadow: ${$fullscreen ? 'none' : theme.boxShadow('s')};
    width: 100%;

    position: relative;

    display: flex;
    flex-direction: column;
    transform-origin: bottom;
  `,
);

const Inner = styled.div<{ $bypassResizer?: boolean }>(
  ({ $bypassResizer }) => css`
    height: 100%;
    width: 100%;

    overflow-y: ${$bypassResizer ? 'auto' : 'scroll'};

    ${scrollBarStyles}
  `,
);

export default IframeLayout;
